<template>
    <div id="netgsm-extensions">
        <div class="page-header">
            <div class="left">
                <h3>{{ $t("message.netgsmExtensionsTitle") }}</h3>
                <p>{{ $t("message.netgsmExtensionsSubtitle") }}</p>
                <small class="websocket-info">
                    <i class="material-icons">info</i>
                    {{ $t("message.netgsmExtensionsWebsocketHint") }}
                </small>

            </div>
            <div class="right">
                <a-button @click="refreshExtensions" :icon="'reload'" :loading="loading">
                    {{ $t("message.refresh") }}
                </a-button>
            </div>
        </div>

        <a-alert :message="$t('message.netgsmExtensionsWebhookTitle')" type="success" class="webhook-alert">
            <div slot="description">
                <div class="webhook-info">
                    <span>
                        <a-icon type="copy" @click="copyWebhookAddress" />
                    </span>
                    <span>{{ getWebhookAddress() }}</span>
                </div>
            </div>
        </a-alert>

        <div class="content">
            <SupsisTable
                :columns="tableColumns"
                :rows="extensions"
                :loading="loading"
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>

    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { WEBPHONE_NETGSM_API } from "../../../../../api/webphone.netgsm";
import log2 from "../../../../../utils/log2";
import CONFIG from "../../../../../config/config";
import { copyText2Clipboard } from "../../../../../utils/utils";

const log = log2("NetGsmExtensions");

export default Vue.extend({
    name: "NetGsmExtensions",
    components: {
        SupsisTable,
        SupsisButton,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            extensions: [],
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        tableColumns() {
            void this.$i18n.locale;
            return this.getCols();
        },
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
        }),
        getWebhookAddress() {
            return `${CONFIG.SOCKET_URL}/webhook/netgsm/${this.site._id}/${this.value._id}`;
        },
        copyWebhookAddress() {
            copyText2Clipboard(this.getWebhookAddress());
            this.$message.success(this.$t("message.webhookUrlCopied"));
        },
        getCols() {
            return [
                {
                    key: "extension",
                    field: "extension",
                    title: this.$t("message.netgsmColExtensionNo"),
                    width: 100,
                    align: "left",
                },
                {
                    key: "displayname",
                    field: "displayname",
                    title: this.$t("message.netgsmColDisplayName"),
                    width: 150,
                    align: "left",
                },
                {
                    key: "transport",
                    field: "transport",
                    title: this.$t("message.netgsmColTransport"),
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <a-tag color={row.transport === "wss" ? "green" : "blue"}>{row.transport}</a-tag>;
                    },
                },
                {
                    key: "recording",
                    field: "recording",
                    title: this.$t("message.netgsmColRecording"),
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={row.recording === "yes" ? "green" : "red"}>
                                {row.recording === "yes" ? this.$t("common.yes") : this.$t("common.no")}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "voicemail",
                    field: "voicemail",
                    title: this.$t("message.netgsmColVoicemail"),
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={row.voicemail === "yes" ? "green" : "red"}>
                                {row.voicemail === "yes" ? this.$t("common.yes") : this.$t("common.no")}
                            </a-tag>
                        );
                    },
                },
            ];
        },
        async loadExtensions() {
            try {
                this.loading = true;
                const response = await WEBPHONE_NETGSM_API.extensions.list();

                if (response && response.extensions) {
                    this.extensions = response.extensions.map((ext, index) => ({
                        ...ext,
                        _id: ext.id || index.toString(),
                    }));
                    this.pagination.total = this.extensions.length;
                    log("NetGSM dahili listesi yüklendi", { count: this.extensions.length });
                } else {
                    this.extensions = [];
                    this.pagination.total = 0;
                }
            } catch (error) {
                console.error("Dahili listesi yüklenirken hata:", error);
                this.$message.error(this.$t("message.netgsmExtensionsLoadError"));
                this.extensions = [];
                this.pagination.total = 0;
            } finally {
                this.loading = false;
            }
        },
        refreshExtensions() {
            this.loadExtensions();
        },
    },
    mounted() {
        this.loadExtensions();
    },
});
</script>

<style scoped lang="scss">
#netgsm-extensions {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .webhook-alert {
        margin-bottom: 20px;

        .webhook-info {
            display: flex;
            align-items: center;
            gap: 10px;
            font-family: monospace;
            font-size: 13px;

            span:first-child {
                cursor: pointer;
                transition: all 0.2s;

                &:hover {
                    color: #1890ff;
                    transform: scale(1.1);
                }
            }
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .actions {
        display: flex;
        gap: 5px;
        justify-content: center;

        .supsis-button {
            background: none;
            border: none;
            padding: 3px;

            .material-icons {
                font-size: 18px;
            }
        }
    }

    .websocket-info {
        display: flex;
        align-items: center;
        gap: 4px;
        color: #007c89;
        font-size: 12px;
        margin-top: 8px;
        font-weight: 500;

        .material-icons {
            font-size: 14px;
        }
    }
}
</style>
